import React, { useState, useEffect } from 'react';
import { 
    Drawer, 
    Box, 
    Typography, 
    IconButton, 
    Divider, 
    Chip, 
    Grid, 
    Paper,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    TableContainer,
    Skeleton,
    Button
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import RestaurantIcon from '@mui/icons-material/Restaurant';
import RoomServiceIcon from '@mui/icons-material/RoomService';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import { RestaurantService } from '../../../api/services/restaurantService';
import { formatCurrency } from '../../../utils/currency';

export default function OrderDetailsDrawer({ open, onClose, orderId, onStatusUpdated, initialOrder }) {
    const [order, setOrder] = useState(initialOrder || null);
    const [loading, setLoading] = useState(false);
    const [statusUpdating, setStatusUpdating] = useState(false);

    useEffect(() => {
        if (open && orderId) {
            if (initialOrder) setOrder(initialOrder);
            fetchOrderDetails();
        } else {
            setOrder(null);
        }
    }, [open, orderId]);

    const fetchOrderDetails = async () => {
        setLoading(!initialOrder);
        try {
            const data = await RestaurantService.getRestaurantOrderDetails(orderId);
            setOrder(data);
        } catch (error) {
            console.error('Failed to fetch order details:', error);
            if (!initialOrder) {
                onClose(); // Gracefully close if no fallback order details exist
            }
        } finally {
            setLoading(false);
        }
    };

    const handleStatusUpdate = async (nextStatus) => {
        setStatusUpdating(true);
        try {
            await RestaurantService.updateOrderStatus(orderId, nextStatus);
            if (typeof onStatusUpdated === 'function') {
                onStatusUpdated();
            }
            await fetchOrderDetails();
        } catch (error) {
            console.error('Failed to update order status:', error);
        } finally {
            setStatusUpdating(false);
        }
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'Served':
            case 'Completed': return 'success';
            case 'Ready': return 'info';
            case 'Preparing':
            case 'In Progress': return 'warning';
            case 'Pending': return 'primary';
            case 'Cancelled': return 'error';
            default: return 'default';
        }
    };

    const getDisplayStatus = (status) => {
        if (status === 'In Progress') return 'Preparing';
        if (status === 'Completed') return 'Served';
        return status;
    };

    const formatDate = (isoString) => {
        if (!isoString) return 'N/A';
        return new Date(isoString).toLocaleString('en-US', { 
            year: 'numeric', month: 'short', day: 'numeric', 
            hour: '2-digit', minute: '2-digit' 
        });
    };

    const renderSkeletons = () => (
        <Box sx={{ p: 3 }}>
            <Skeleton variant="rectangular" width="100%" height={100} sx={{ borderRadius: 2, mb: 3 }} />
            <Skeleton variant="rectangular" width="100%" height={80} sx={{ borderRadius: 2, mb: 3 }} />
            <Skeleton variant="rectangular" width="100%" height={200} sx={{ borderRadius: 2, mb: 3 }} />
            <Skeleton variant="rectangular" width="100%" height={80} sx={{ borderRadius: 2 }} />
        </Box>
    );

    return (
        <Drawer 
            anchor="right" 
            open={open} 
            onClose={onClose}
            sx={{ zIndex: (theme) => theme.zIndex.modal + 100 }}
            PaperProps={{ sx: { width: { xs: '100%', sm: 600 }, bgcolor: '#fbfbfb' } }}
        >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 3, borderBottom: '1px solid #eee', bgcolor: 'white' }}>
                <Typography variant="h6" fontWeight="bold">Order Details</Typography>
                <IconButton onClick={onClose} size="small"><CloseIcon /></IconButton>
            </Box>

            {loading ? renderSkeletons() : order && (
                <Box sx={{ p: 3 }}>
                    
                    {/* Order Administrative Header */}
                    <Paper sx={{ p: 3, borderRadius: 2, border: '1px solid #eee', mb: 3, elevation: 0 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                            <Box>
                                <Typography variant="caption" color="text.secondary" display="block">Ticket Reference Number</Typography>
                                <Typography variant="h5" fontWeight="bold">
                                    {order.orderId || order.ticketNumber || order.id?.substring(0, 8).toUpperCase()}
                                </Typography>
                            </Box>
                            <Chip 
                                label={getDisplayStatus(order.status || 'Pending')} 
                                color={getStatusColor(order.status || 'Pending')} 
                                variant="filled"
                                sx={{ fontWeight: 'bold' }}
                            />
                        </Box>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                            Order Placement Datetime: <b>{formatDate(order.orderTime || order.createdAt || order.placedAt)}</b>
                        </Typography>
                        
                        {/* Status Transition Action Row */}
                        {['Pending', 'In Progress', 'Ready', 'Preparing'].includes(order.status) && (
                            <Box sx={{ borderTop: '1px solid #f0f0f0', pt: 2, display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                                {(order.status === 'Pending') && (
                                    <Button
                                        variant="contained"
                                        color="primary"
                                        size="small"
                                        onClick={() => handleStatusUpdate('In Progress')}
                                        disabled={statusUpdating}
                                        sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 'bold' }}
                                    >
                                        Accept & Prepare Order
                                    </Button>
                                )}
                                {(order.status === 'In Progress' || order.status === 'Preparing') && (
                                    <Button
                                        variant="contained"
                                        color="warning"
                                        size="small"
                                        onClick={() => handleStatusUpdate('Ready')}
                                        disabled={statusUpdating}
                                        sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 'bold' }}
                                    >
                                        Mark as Ready
                                    </Button>
                                )}
                                {(order.status === 'Ready') && (
                                    <Button
                                        variant="contained"
                                        color="primary"
                                        size="small"
                                        onClick={() => handleStatusUpdate('Completed')}
                                        disabled={statusUpdating}
                                        sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 'bold' }}
                                    >
                                        Deliver & Complete Order
                                    </Button>
                                )}
                            </Box>
                        )}
                    </Paper>

                    {/* Room & Guest Context */}
                    <Paper sx={{ p: 3, borderRadius: 2, border: '1px solid #eee', mb: 3, elevation: 0 }}>
                        <Typography variant="subtitle2" fontWeight="bold" sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                            <RoomServiceIcon fontSize="small" sx={{ mr: 1, color: 'primary.main' }} /> Room & Guest Context
                        </Typography>
                        <Grid container spacing={2}>
                            <Grid item xs={6}>
                                <Typography variant="caption" color="text.secondary" display="block">Assigned Room Profile</Typography>
                                <Typography variant="body1" fontWeight="bold">
                                    {order.room?.roomNumber ? `Room ${order.room.roomNumber}` : 'Direct Sale'}
                                </Typography>
                            </Grid>
                            <Grid item xs={6}>
                                <Typography variant="caption" color="text.secondary" display="block">Guest Folio Targeting</Typography>
                                <Typography variant="body1" fontWeight="bold">
                                    {order.guest?.name || 'Walk-In / Unregistered Guest'}
                                </Typography>
                            </Grid>
                            <Grid item xs={12}>
                                <Typography variant="caption" color="text.secondary" display="block">Folio Charge Reference</Typography>
                                <Typography variant="body1" fontWeight="bold" color="primary.main">
                                    {order.folioChargeId || order.folioReference || 'Not Posted to Folio'}
                                </Typography>
                            </Grid>
                        </Grid>
                    </Paper>

                    {/* Stage Timestamps */}
                    <Paper sx={{ p: 3, borderRadius: 2, border: '1px solid #eee', mb: 3, elevation: 0 }}>
                        <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 2 }}>
                            Workflow Stage Timestamps
                        </Typography>
                        <Grid container spacing={2}>
                            <Grid item xs={4}>
                                <Typography variant="caption" color="text.secondary" display="block">Approved At</Typography>
                                <Typography variant="body2">{formatDate(order.approvedAt || order.stageTimestamps?.approved)}</Typography>
                            </Grid>
                            <Grid item xs={4}>
                                <Typography variant="caption" color="text.secondary" display="block">Plated At</Typography>
                                <Typography variant="body2">{formatDate(order.platedAt || order.stageTimestamps?.plated)}</Typography>
                            </Grid>
                            <Grid item xs={4}>
                                <Typography variant="caption" color="text.secondary" display="block">Served At</Typography>
                                <Typography variant="body2">{formatDate(order.servedAt || order.stageTimestamps?.served)}</Typography>
                            </Grid>
                        </Grid>
                    </Paper>

                    {/* Line-Items Table Array */}
                    <Paper sx={{ p: 0, borderRadius: '12px', border: '1px solid #eaecf0', mb: 3, elevation: 0, overflow: 'hidden' }}>
                        <Box sx={{ p: 2, borderBottom: '1px solid #eaecf0', bgcolor: '#f8fafc' }}>
                            <Typography variant="subtitle2" fontWeight="bold" sx={{ display: 'flex', alignItems: 'center' }}>
                                <RestaurantIcon fontSize="small" sx={{ mr: 1, color: 'primary.main' }} /> Ordered Items Ledger
                            </Typography>
                        </Box>
                        <TableContainer sx={{ border: 'none', borderRadius: 0, boxShadow: 'none' }}>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Menu Item Name</TableCell>
                                        <TableCell align="center">Qty</TableCell>
                                        <TableCell align="right">Unit Price</TableCell>
                                        <TableCell align="right">Subtotal</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {(order.items || order.orderItems) && (order.items || order.orderItems).length > 0 ? (order.items || order.orderItems).map((item, idx) => (
                                        <TableRow key={item.id || idx}>
                                            <TableCell>{item.menuItem?.name || item.name || 'Unknown Item'}</TableCell>
                                            <TableCell align="center">{item.quantity}</TableCell>
                                            <TableCell align="right">{formatCurrency(item.unitPrice || item.price)}</TableCell>
                                            <TableCell align="right"><b>{formatCurrency((item.quantity || 1) * (item.unitPrice || item.price || 0))}</b></TableCell>
                                        </TableRow>
                                    )) : (
                                        <TableRow>
                                            <TableCell colSpan={4} align="center" sx={{ py: 3 }}><Typography color="text.secondary">No items found in this order.</Typography></TableCell>
                                        </TableRow>
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Paper>

                    {/* Financial Summary Block */}
                    <Paper sx={{ p: 3, borderRadius: 2, border: '1px solid #eee', elevation: 0, bgcolor: 'primary.main', color: 'white' }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center' }}>
                                <AttachMoneyIcon sx={{ mr: 1 }} /> Master Ticket Total
                            </Typography>
                            <Typography variant="h4" fontWeight="bold">
                                {formatCurrency(order.totalAmount || order.amount)}
                            </Typography>
                        </Box>
                    </Paper>

                </Box>
            )}
        </Drawer>
    );
}
